import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import "./SocialLinks.scss";
import { Skeleton, Typography } from "antd";
import {
  FacebookOutlined,
  YoutubeOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  XOutlined,
  TikTokOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { getSocialLinks } from "../../../../features/advertisements/advertisementsActions";

function colorWithAlpha(color, alpha) {
  if (!color) return `rgba(0, 0, 0, ${alpha})`;
  if (color.startsWith("rgba")) {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
  }
  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(0, 0, 0, ${alpha})`;
}

const ICON_MAP = {
  youtube: { icon: YoutubeOutlined, color: "#FF0000" },
  facebook: { icon: FacebookOutlined, color: "#1877F2" },
  twitter: { icon: XOutlined, color: null },
  linkedin: { icon: LinkedinOutlined, color: "#0077B5" },
  instagram: { icon: InstagramOutlined, color: "#f58529" },
  tiktok: { icon: TikTokOutlined, color: "#000000" },
};

const FALLBACK_LINKS = [
  {
    key: "youtube",
    label: "YouTube",
    url: "https://www.youtube.com/",
  },
  {
    key: "facebook",
    label: "Facebook",
    url: "https://www.facebook.com/",
  },
  {
    key: "twitter",
    label: "X (Twitter)",
    url: "https://www.x.com/",
  },
  {
    key: "linkedin",
    label: "Linkedin",
    url: "https://www.linkedin.com/",
  },
  {
    key: "instagram",
    label: "Instagram",
    url: "https://www.instagram.com/",
  },
  {
    key: "tiktok",
    label: "TikTok",
    url: "https://www.tiktok.com/",
  },
];

export default function SocialLinks({ token }) {
  const dispatch = useDispatch();
  const [links, setLinks] = useState(FALLBACK_LINKS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(getSocialLinks())
      .then((res) => {
        if (
          res.type === "get_social_links/fulfilled" &&
          Array.isArray(res.payload) &&
          res.payload.length > 0
        ) {
          setLinks(res.payload);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch]);

  return (
    <div className="social-links">
      <div className="social-links__header">
        <div className="social-links__header__accent" />
        <Typography.Text
          className="social-links__title"
          strong
          ellipsis={{
            tooltip: {
              title: "Follow Us Online",
            },
          }}>
          Follow Us Online
        </Typography.Text>
      </div>
      {loading ? (
        <div className="social-links__list">
          {FALLBACK_LINKS.map((_, i) => (
            <div key={i} className="social-links__skeleton-item">
              <Skeleton.Avatar active size={40} shape="circle" />
              <Skeleton.Button
                active
                size="small"
                style={{ width: 60, minWidth: 60, height: 16 }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="social-links__list">
          {links.map((social) => {
            const mapping = ICON_MAP[social.key] || {};
            const IconComponent = mapping.icon || LinkOutlined;
            return (
              <a
                key={social.key}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-links__link">
                <div
                  className="social-links__item"
                  data-brand={social.key}
                  style={{
                    "--hover-bg": colorWithAlpha(mapping.color, 0.1),
                    "--hover-border": colorWithAlpha(mapping.color, 0.2),
                  }}>
                  <button className="social-links__item__btn" type="button">
                    <IconComponent
                      style={{
                        color: mapping.color || token.colorText,
                      }}
                    />
                  </button>
                  <Typography.Text
                    ellipsis={{
                      tooltip: {
                        title: social.label || "Link",
                      },
                    }}>
                    {social.label || "Link"}
                  </Typography.Text>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
